import {
  normalizeTopicLocale,
  type TopicLocale,
} from "@shared/topic-taxonomy";
import { useEffect, useMemo, useState } from "react";

export const TOPIC_LOCALE_STORAGE_KEY = "ozk_topic_locale";
const TOPIC_LOCALE_CHANGE_EVENT = "ozk-topic-locale-change";

export function detectBrowserTopicLocale(): TopicLocale {
  if (typeof navigator === "undefined") return "ko";

  const languages =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];
  const firstSupported = languages.find((language) =>
    /^(ko|en)(-|$)/i.test(language),
  );

  return firstSupported ? normalizeTopicLocale(firstSupported) : "en";
}

export function getStoredTopicLocale(): TopicLocale | null {
  if (typeof window === "undefined") return null;

  const value = window.localStorage.getItem(TOPIC_LOCALE_STORAGE_KEY);
  return value === "ko" || value === "en" ? value : null;
}

export function useTopicLocale() {
  const initialLocale = useMemo(
    () => getStoredTopicLocale() ?? detectBrowserTopicLocale(),
    [],
  );
  const [locale, setLocaleState] = useState<TopicLocale>(initialLocale);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== TOPIC_LOCALE_STORAGE_KEY) return;
      if (event.newValue === "ko" || event.newValue === "en") {
        setLocaleState(event.newValue);
      }
    };

    const handleLocaleChange = (event: Event) => {
      const nextLocale = (event as CustomEvent<TopicLocale>).detail;
      if (nextLocale === "ko" || nextLocale === "en") {
        setLocaleState(nextLocale);
      }
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(TOPIC_LOCALE_CHANGE_EVENT, handleLocaleChange);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(TOPIC_LOCALE_CHANGE_EVENT, handleLocaleChange);
    };
  }, []);

  const setLocale = (nextLocale: TopicLocale) => {
    setLocaleState(nextLocale);
    window.localStorage.setItem(TOPIC_LOCALE_STORAGE_KEY, nextLocale);
    window.dispatchEvent(
      new CustomEvent<TopicLocale>(TOPIC_LOCALE_CHANGE_EVENT, {
        detail: nextLocale,
      }),
    );
  };

  return {
    locale,
    setLocale,
    isKorean: locale === "ko",
  };
}
